import classnames from "classnames";
import fuzzysort from "fuzzysort";
import React, { useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { Portal } from "@lana-commerce/core/react/components/Portal";
import { RootCloseWrapper } from "@lana-commerce/core/react/components/RootCloseWrapper";
import { generateUUID } from "@lana-commerce/core/generateUUID";

import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu";
import { KeyboardArrowDown } from "./icons/KeyboardArrowDown";
import { fuzzyHighlight } from "../utils/fuzzyHighlight";
import { getInputHelpers, renderInputMessage } from "./Input";
import { usePopperSameWidth } from "../utils/popper";

export interface DropdownItem {
  label: string;
  value: string;
}

interface DropdownProps {
  items: DropdownItem[];
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  message?:
    | { kind: "warning"; text: string | JSX.Element }
    | { kind: "error"; text: string | JSX.Element }
    | { kind: "success"; text: string | JSX.Element };
}

interface InnerState {
  dropdownVisible: boolean;
  search: string;
  hover: string | undefined;
}

interface FuzzyItem extends DropdownMenuItem {
  originalLabel: string;
}

function fuzzySearch(search: string, items: DropdownItem[]): FuzzyItem[] {
  if (!search) return items.map((item) => ({ ...item, originalLabel: item.label }));
  const result = fuzzysort.go(search, items, { key: "label" });
  return result.map((item) => ({
    value: item.obj.value,
    label: fuzzyHighlight(item),
    originalLabel: item.obj.label,
  }));
}

export const Dropdown = (props: DropdownProps) => {
  const shouldFocusDiv = useRef<boolean>(false);
  const lastFocused = useRef<HTMLInputElement>(null);
  const uuid = useState(() => generateUUID())[0];
  const initialInnerState = {
    dropdownVisible: false,
    search: "",
    hover: props.items.length > 0 ? props.items[0].value : "",
  };
  const [st, setSt] = useState<InnerState>(initialInnerState);
  const pp = usePopperSameWidth("bottom");
  const onRootClose = () => {
    setSt({ ...st, dropdownVisible: false });
  };
  const icon = {
    kind: "right" as const,
    component: (props: React.SVGProps<SVGSVGElement>) => (
      <KeyboardArrowDown
        {...props}
        className={classnames(props.className, "transition-transform duration-300", {
          "rotate-180": st.dropdownVisible,
        })}
      />
    ),
  };
  const h = getInputHelpers({
    kind: "text",
    disabled: props.disabled,
    icon,
    message: props.message,
  });
  const m = props.message;
  const items = fuzzySearch(st.search, props.items);
  const item = items.find((item) => item.value === props.value);
  return (
    <>
      <div>
        {props.label ? (
          <label className="block text-off-black txt-field-title mb-2" htmlFor={props.id}>
            {props.label}
          </label>
        ) : null}
        <RootCloseWrapper tag={uuid} onRootClose={onRootClose} disabled={!st.dropdownVisible}>
          <div ref={pp.setReferenceElement} className={h.wrapperClass}>
            {!st.dropdownVisible ? (
              <div
                ref={(v) => {
                  if (shouldFocusDiv.current && v && lastFocused.current !== v) {
                    (shouldFocusDiv as any).current = false;
                    (lastFocused as any).current = v;
                    v.focus();
                  }
                }}
                id={props.id}
                className={classnames(h.inputClass, "whitespace-nowrap overflow-hidden text-ellipsis")}
                onClick={() => {
                  setSt({ ...initialInnerState, dropdownVisible: true });
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Tab") {
                    setSt({ ...initialInnerState, dropdownVisible: true });
                    e.preventDefault();
                  }
                }}
                tabIndex={0}
              >
                {item?.originalLabel ||
                  (props.placeholder ? (
                    <span className="text-placeholder">{props.placeholder}</span>
                  ) : (
                    <span>&nbsp;</span>
                  ))}
              </div>
            ) : (
              <input
                size={1}
                ref={(v) => {
                  if (v && lastFocused.current !== v) {
                    (lastFocused as any).current = v;
                    v.focus();
                  }
                }}
                autoComplete="off"
                id={props.id}
                value={st.search}
                onChange={(e) => {
                  const result = fuzzysort.go(e.target.value, props.items, { key: "label" });
                  const hover = result.length > 0 ? result[0].obj.value : "";
                  setSt({ ...st, hover, search: e.target.value });
                }}
                onKeyDown={(e) => {
                  if (items.length === 0) return;
                  if (e.key === "ArrowDown") {
                    if (st.hover === undefined) {
                      setSt({ ...st, hover: items[0].value });
                      e.preventDefault();
                    } else {
                      const idx = items.findIndex((item) => item.value === st.hover);
                      if (idx !== -1 && idx < items.length - 1) {
                        setSt({ ...st, hover: items[idx + 1].value });
                        e.preventDefault();
                      }
                    }
                  } else if (e.key === "ArrowUp") {
                    if (st.hover === undefined) {
                      setSt({ ...st, hover: items[items.length - 1].value });
                      e.preventDefault();
                    } else {
                      const idx = items.findIndex((item) => item.value === st.hover);
                      if (idx !== -1 && idx > 0) {
                        setSt({ ...st, hover: items[idx - 1].value });
                        e.preventDefault();
                      }
                    }
                  } else if (e.key === "Enter") {
                    if (st.hover !== undefined) {
                      shouldFocusDiv.current = true;
                      props.onChange(st.hover);
                      setSt({
                        ...st,
                        dropdownVisible: false,
                      });
                    }
                    // always prevent default on enter, it will cause form submit otherwise
                    e.preventDefault();
                  } else if (e.key === "Tab") {
                    if (st.hover !== undefined) {
                      shouldFocusDiv.current = true;
                      props.onChange(st.hover);
                      setSt({
                        ...st,
                        dropdownVisible: false,
                      });
                    }
                  }
                }}
                disabled={props.disabled}
                className={h.inputClass}
                type="text"
              />
            )}
            {h.wrapIcon(icon.component({ className: h.iconClass }))}
          </div>
        </RootCloseWrapper>
        {m ? renderInputMessage(m) : null}
      </div>
      <Portal>
        <CSSTransition in={st.dropdownVisible} timeout={200} mountOnEnter unmountOnExit>
          <RootCloseWrapper tag={uuid} onRootClose={onRootClose}>
            <div
              className="popup-menu bg-white drop-shadow-only-1"
              ref={pp.setPopperElement}
              style={pp.style}
              {...pp.attrs}
            >
              <DropdownMenu
                maxHeight={5}
                items={items}
                onClick={(value) => {
                  shouldFocusDiv.current = true;
                  props.onChange(value);
                  setSt({
                    ...st,
                    dropdownVisible: false,
                  });
                }}
                hover={st.hover}
                onHover={(v) => {
                  setSt({ ...st, hover: v });
                }}
                active={props.value}
              />
            </div>
          </RootCloseWrapper>
        </CSSTransition>
      </Portal>
    </>
  );
};
